// This file is used to map the fields for the NGL North Carolina Cremation Authorization Form
import { getNGLFieldNames } from '@/components/documents/Foxit/FormMapping/FieldNameProviders/NGLFieldNameProvider'
import { useFormFillHelper } from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import { useIdentitySetup } from '@/components/documents/Foxit/FormMapping/composables/IdentityFields'
import type { CartItemAndLineItem } from '@/mixins/merchandise'

export default class NGLNCCremationAuthorizationForm {
	private fieldNameProvider = getNGLFieldNames()

	// Standard list of fields for this form.
	// These values point to field names in the fieldNameProvider, as well as functions to get the value for the field via the fieldValueProvider
	private fields: (keyof typeof this.fieldNameProvider)[] = [
		// Identity
		'insuredFullName',
		'funeralHomeName',
		'funeralHomeFullAddress',
		'funeralHomeRepresentativePrintedName',
		'insuredAge',
		'insuredDateOfBirth',
		'insuredFullName',
		'insuredHospiceYesOrNo', // Comes from Health Question 1
	]

	private unfilledFields = [
		'cematoryLicenseeFullAddress',
		'cematoryLicenseeName',
		'conflictingReligiousPractices',
		'containsHazardousDeviceNoCheck',
		'containsHazardousDeviceYesCheck',
		'containsPacemkerNoCheck',
		'containsPacemkerYesCheck',
		'cremationApprovalDate',
		'cremationApprovalPrintedName',
		'cremationApprovalSpecialInstructions',
		'cremationApprovalSpecialInstructions1',
		'crematoryFullAddress',
		'crematoryLicenseeRepresentativePrintedName',
		'crematoryLicenseeRepresentativeSignatureDate',
		'crematoryName',
		'deliveryRemainsCemeteryCheck',
		'deliveryRemainsCemeteryInstructions',
		'deliveryRemainsCemeteryName',
		'deliveryRemainsDesignatedPersonsCheck',
		'deliveryRemainsDesignatedPersonsName',
		'deliveryRemainsDesignatedPersonsName1',
		'deliveryRemainsDesignatedPersonsName2',
		'deliveryRemainsDesignatedPersonsName3',
		'deliveryRemainsDesignatedPersonsRelationship',
		'deliveryRemainsDesignatedPersonsRelationship1',
		'deliveryRemainsDesignatedPersonsRelationship2',
		'deliveryRemainsDesignatedPersonsRelationship3',
		'deliveryRemainsDesignatedPersonsSpecialInstructions',
		'deliveryRemainsOtherCheck',
		'deliveryRemainsOtherDesc',
		'deliveryRemainsOtherDesc1',
		'deliveryRemainsUSPostalServiceAddress',
		'deliveryRemainsUSPostalServiceCheck',
		'deliveryRemainsUSPostalServiceName',
		'individualConfirmingIdentityofDecedentPrintedName',
		'insuredDateofDeath',
		'insuredInfectiousDiseaseDeathYesOrNo',
		'insuredPlaceofDeath',
		'insuredTimeofDeath',
		'legallyAuthorizedPerson1FullAddress',
		'legallyAuthorizedPerson1Phone',
		'legallyAuthorizedPerson1PrintedName',
		'legallyAuthorizedPerson1RelationshipToDecedent',
		'legallyAuthorizedPerson2FullAddress',
		'legallyAuthorizedPerson2Phone',
		'legallyAuthorizedPerson2PrintedName',
		'legallyAuthorizedPerson2RelationshipToDecedent',
		'legallyAuthorizedPerson3FullAddress',
		'legallyAuthorizedPerson3Phone',
		'legallyAuthorizedPerson3PrintedName',
		'legallyAuthorizedPerson3RelationshipToDecedent',
		'legallyAuthorizedPersonFullAddress',
		'legallyAuthorizedPersonPhone',
		'legallyAuthorizedPersonPrintedName',
		'legallyAuthorizedPersonRelationshipToDecedent',
		'medicalExaminerAuthYesOrNo',
		'notaryCommissionExpiresDate',
		'notaryCounty',
		'notaryPrintedName',
		'notaryState',
		'personsAttemptedToBeContacted1',
		'personsAttemptedToBeContacted2',
		'personsAttemptedToBeContacted3',
		'personsAttemptedToBeContacted4',
		'survivorName',
		'survivorName1',
		'survivorName2',
		'survivorName3',
		'witness1FullAddress',
		'witness1PrintedName',
		'witnessFullAddress',
		'witnessPrintedName',
	]

	// Setup prior to building form filling array.
	constructor(planHolderId: string) {
		const { identitySetup } = useIdentitySetup()
		const { setFieldNameProvider } = useFormFillHelper()

		setFieldNameProvider(this.fieldNameProvider)
		identitySetup(planHolderId, ['planHolder', 'funeralHome', 'salesContact'])
	}

	// Build form filling array.
	getFormMappings() {
		const { formatFields, mapFields } = useFormFillHelper()
		const mainFields = mapFields(this.fields)

		return formatFields([...mainFields])
	}
}
